import { Routes, Route, BrowserRouter } from 'react-router-dom';
import React, { lazy, Suspense, useEffect } from 'react';
import { getDeviceInformation, getIpDetails, routesMapping } from 'utilities/utilities_exports';
import Layout from 'layouts';
import ErrorPage from 'pages/System/404';
import PrivateRoute from 'routes/PrivateRoute';
import clevertap from 'clevertap-web-sdk';
import { isEmpty as _isEmpty } from 'lodash-es';
import LandingPageRedirect from 'routes/LandingPageRedirect';
import jwt_decode from 'jwt-decode';
import Api from 'redux/api';

const routes = [
  {
    path: '/login',
    Component: lazy(() => import(/* webpackChunkName: "Loader" */ 'pages/LoginLoader'))
  },
  {
    path: '/demo-login',
    Component: lazy(() => import(/* webpackChunkName: "Demo-Loader" */ 'Modules/Demo/DemoLoader'))
  },
  {
    path: '/forgotPassword',
    Component: lazy(() => import(/* webpackChunkName: "ForgotPassword" */ 'pages/ForgotPasswordContainer'))
  },
  {
    path: '/signup',
    Component: lazy(() => import(/* webpackChunkName: "Signup" */ 'pages/SignUpContainer'))
  },
  {
    path: '/privacyPolicy',
    Component: lazy(() => import(/* webpackChunkName: "PrivacyPolicy" */ 'ExternalPages/PrivacyPolicy'))
  },
  {
    path: '/cookiePolicy',
    Component: lazy(() => import(/* webpackChunkName: "CookiePolicy" */ 'ExternalPages/CookiesPolicy'))
  },
  {
    path: '/termConditions',
    Component: lazy(() => import(/* webpackChunkName: "TermConditions" */ 'ExternalPages/TermConditions'))
  },
  {
    path: '/error',
    Component: lazy(() => import(/* webpackChunkName: "Error" */ 'pages/Error'))
  },
  {
    path: '/analyze',
    Component: lazy(() => import(/* webpackChunkName: "Dashboard" */ 'pages/Dashboard'))
  },
  {
    path: '/analyze/:id',
    Component: lazy(() => import(/* webpackChunkName: "Dashboard" */ 'pages/Dashboard'))
  },
  {
    path: '/predict',
    Component: lazy(() =>
      import(/* webpackChunkName: "RecommendationContainer" */ 'Modules/Predict/pages/RecommendationContainer')
    ),
    isProtected: true,
    routeId: 'nav_predict'
  },
  {
    path: '/predict/details/:id',
    Component: lazy(() =>
      import(/* webpackChunkName: "RecommendationDetails" */ 'Modules/Predict/pages/RecommendationDetailsContainer')
    ),
    isProtected: true,
    routeId: 'nav_predict'
  },
  {
    path: '/predict/list',
    Component: lazy(() =>
      import(/* webpackChunkName: "RecommendationContainer" */ 'Modules/Predict/pages/RecommendationContainer')
    ),
    isProtected: true,
    routeId: 'nav_predict'
  },

  // !- Execute Module
  {
    path: '/executor/:id/:site/:tempId',
    Component: lazy(() => import(/* webpackChunkName: "ExecuteContainer" */ 'pages/ExecuteContainer'))
  },
  {
    path: '/executor/:id/:site/:tempId/:action',
    Component: lazy(() => import(/* webpackChunkName: "ExecuteContainer" */ 'pages/ExecuteContainer'))
  },
  {
    path: '/executor/:id/:action',
    Component: lazy(() => import(/* webpackChunkName: "executeContainer" */ 'pages/ExecuteContainer'))
  },
  {
    path: '/executor/chat',
    Component: lazy(() =>
      import(/* webpackChunkName: "ChatAggregator" */ 'Modules/Execute/pages/ChatAggregatorContainer')
    )
  },
  {
    path: '/execute',
    Component: lazy(() => import(/* webpackChunkName: "ExecuteIndex" */ 'Modules/Execute/index'))
  },
  {
    path: '/executor/:id',
    Component: lazy(() => import(/* webpackChunkName: "ExecuteContainer" */ 'pages/ExecuteContainer'))
  },
  {
    path: '/analyze/insights',
    Component: lazy(() => import(/* webpackChunkName: "Insights" */ 'pages/InsightsContainer')),
    isProtected: true,
    routeId: 'insights'
  },
  {
    path: '/analyze/insights/:selectedTab/:subType',
    Component: lazy(() => import(/* webpackChunkName: "Insights" */ 'pages/InsightsContainer')),
    isProtected: true,
    routeId: 'insights'
  },
  {
    path: '/analyze/insights/insight-details/:id',
    Component: lazy(() => import(/* webpackChunkName: "Insights" */ 'pages/InsightsContainer')),
    isProtected: true,
    routeId: 'insights'
  },
  {
    path: '/execute/segments/add',
    Component: lazy(() => import(/* webpackChunkName: "SegmentContainer" */ 'pages/SegmentContainer/add')),
    isProtected: true,
    routeId: 'segments'
  },
  {
    path: '/profile/business-profile',
    Component: lazy(() => import(/* webpackChunkName: "AccountSettingsContainer" */ 'pages/AccountSettingsContainer'))
  },
  {
    path: '/analyze/competitive-dashboard/:id',
    Component: lazy(() => import(/* webpackChunkName: "CompetitorsDetails" */ 'pages/CompetitorsDetailsContainer')),
    isProtected: true,
    routeId: 'competitive-dashboard'
  },
  {
    path: '/access-denied',
    Component: lazy(() => import(/* webpackChunkName: "AccessDenied" */ 'pages/AccessDeniedContainer'))
  },
  {
    path: '/analyze/finance-invoice',
    Component: lazy(() => import(/* webpackChunkName: "Invoice" */ 'pages/InvoiceContainer')),
    isProtected: true,
    routeId: 'finance-invoice'
  },
  {
    path: '/analyze/finance-invoice/:id',
    Component: lazy(() => import(/* webpackChunkName: "Invoice" */ 'pages/InvoiceDetailsContainer')),
    isProtected: true,
    routeId: 'finance-invoice'
  },
  {
    path: '/analyze/finance-reconciliation/stores',
    Component: lazy(() => import(/* webpackChunkName: "Reconciliation" */ 'pages/ReconciliationContainer')),
    isProtected: true,
    routeId: 'finance-reconciliation'
  },
  {
    path: '/analyze/finance-orderIncome/details',
    Component: lazy(() =>
      import(/* webpackChunkName: "OrderIncomeDetailsContainer" */ 'pages/OrderIncomeDetailsContainer')
    ),
    isProtected: true,
    routeId: 'finance-orderIncome'
  },
  {
    path: '/analyze/finance-reconciliation/summaryBreakdown',
    Component: lazy(() => import(/* webpackChunkName: "SummaryBreakdown" */ 'pages/ReconciliationDetailsContainer')),
    isProtected: true,
    routeId: 'finance-reconciliation'
  },
  {
    path: `/analyze/deep-dive/metricDefinition`,
    Component: lazy(() => import(/* webpackChunkName: "DeepDiveMetrics" */ 'pages/DeepDiveMetrics')),
    isProtected: true,
    routeId: 'workspace'
  },
  {
    path: '/planDetails',
    Component: lazy(() => import(/* webpackChunkName: "PlanDetail" */ 'pages/PlanDetailContainer'))
  },
  {
    path: '/pricingDetails',
    Component: lazy(() => import('pages/NewSubscriptionPlanContainer'))
  },

  // !- Connect Module
  {
    path: `/connect/journal`,
    Component: lazy(() => import(/* webpackChunkName: "Connector" */ 'Modules/Connect/pages/ConnectorContainer')),
    isProtected: true,
    routeId: 'nav_connect'
  },
  {
    path: `/connect/journal-beta`,
    Component: lazy(() => import(/* webpackChunkName: "Connector" */ 'Modules/Connect/pages/ConnectorContainer')),
    isProtected: true,
    routeId: 'nav_connect'
  },
  {
    path: '/connect/store-group',
    Component: lazy(() => import(/* webpackChunkName: "StoreGroup" */ 'Modules/Connect/pages/StoreGroup')),
    isProtected: true,
    routeId: 'store-group'
  },
  {
    path: '/connect/connected-stores',
    Component: lazy(() => import(/* webpackChunkName: "ConnectedStores" */ 'Modules/Connect/pages/ConnectedStores')),
    isProtected: true,
    routeId: 'connected-stores'
  },
  {
    path: '/connect/mapping',
    Component: lazy(() => import(/* webpackChunkName: "Mapping" */ 'Modules/Connect/pages/MappingContainer')),
    isProtected: true,
    routeId: 'mapping'
  },
  {
    path: '/connect/mapping/:id',
    Component: lazy(() => import(/* webpackChunkName: "Mapping" */ 'Modules/Connect/pages/MappingContainer')),
    isProtected: true,
    routeId: 'mapping'
  },
  {
    path: `/connect/offline-orders`,
    Component: lazy(() => import('Modules/Execute/pages/OfflineOrderContainer')),
    isProtected: true,
    routeId: 'nav_connect'
  },
  {
    path: `${routesMapping['connect']}`,
    Component: lazy(() =>
      import(/* webpackChunkName: "ConnectContainer" */ 'Modules/Connect/pages/ConnectContainer/ConnectedStoreList')
    ),
    isProtected: true,
    routeId: 'connect'
  },
  {
    path: `${routesMapping['connect']}/:id`,
    Component: lazy(() =>
      import(
        /* webpackChunkName: "ConnectDetailsContainer" */ 'Modules/Connect/pages/ConnectContainer/ConnectDetailsContainer/ConnectDetailsContainer'
      )
    ),
    isProtected: true,
    routeId: 'connect'
  },
  {
    path: `${routesMapping['connect']}/:id/custom-flow`,
    Component: lazy(() =>
      import(/* webpackChunkName: "CustomFlowSettingContainer" */ 'Modules/Connect/pages/CustomFlowSettingContainer')
    )
  },
  {
    path: `${routesMapping['home']}`,
    Component: lazy(() => import(/* webpackChunkName: "HomePageContainer" */ 'Modules/Home/pages/HomePageContainer'))
  },

  // !- chartBuilder Module
  {
    path: `/chart-builder`,
    Component: lazy(() =>
      import(/* webpackChunkName: "ChartBuilder" */ 'Modules/ChartBuilder/pages/ChartBuilderContainer')
    )
  },
  {
    path: '/extract',
    Component: lazy(() => import(/* webpackChunkName: "extract" */ 'Modules/Extract/pages/ExtractWrapper'))
  },
  {
    path: '/executor/preview/:id',
    Component: lazy(() => import('Modules/Execute/subModules/ListingBatch/components/PreviewListing/PreviewDetail'))
  },
  {
    path: '/hoppr',
    Component: lazy(() => import(/* webpackChunkName: "hoppr" */ 'Modules/Hopper/pages/HopprContainer')),
    routeId: 'hoppr'
  }
];
clevertap.init(process.env.REACT_APP_CLEVERTAP_KEY, process.env.REACT_APP_CLEVERTAP_REGION);
clevertap.setLogLevel(3);
const auth = new Api();

const Router = props => {
  const checkAdminSideLoginAndFireCleverTapEvent = async () => {
    const accessToken = auth.getAccessToken();
    if (accessToken) {
      const parsedAccessToken = jwt_decode(accessToken);
      let adminDomain = new URL(process.env.REACT_APP_ADMIN_URL).hostname;
      let referrerDomain = '';
      if (document.referrer !== '') {
        referrerDomain = new URL(document.referrer).hostname;
      }

      if (adminDomain && referrerDomain === adminDomain) {
        const deviceInformation = getDeviceInformation();
        let ipDetails = await getIpDetails();
        clevertap.onUserLogin?.push({
          Site: {
            Identity: parsedAccessToken?.merchantID + '-' + parsedAccessToken?.userName.replace('@', '~'),
            mode: 'admin',
            seller_id: parsedAccessToken?.merchantID,
            Name: parsedAccessToken?.userName,
            Email: parsedAccessToken?.userName,
            'user properties': JSON.stringify(parsedAccessToken),
            latitude: ipDetails?.latitude,
            longitude: ipDetails?.longitude,
            country: ipDetails?.country_name,
            ip: ipDetails?.ip
          }
        });
        if (ipDetails?.latitude && ipDetails?.longitude) {
          clevertap.getLocation(ipDetails?.latitude, ipDetails?.longitude);
        }
        clevertap.event?.push('Login', {
          status: 'success',
          mode: 'admin',
          seller_id: parsedAccessToken?.merchantID,
          device: deviceInformation?.deviceType,
          browser: deviceInformation?.browser?.name,
          country: ipDetails?.country_name,
          is_internal: true
        });
      }
    }
  };
  useEffect(() => {
    if (process.env.node_env === 'production') {
      checkAdminSideLoginAndFireCleverTapEvent();
    }
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<></>}>
        <Layout>
          <Routes>
            <Route path='/' element={<LandingPageRedirect />} />
            {routes.map(({ path, Component, isProtected, routeId }) => (
              <Route
                key={path}
                path={path}
                element={
                  isProtected ? (
                    <PrivateRoute routeId={routeId}>
                      <Component />
                    </PrivateRoute>
                  ) : (
                    <Component />
                  )
                }
              />
            ))}

            {/* Redirect non-matching routes to the 404 page */}
            <Route path='*' element={<ErrorPage errorCode='404' errorTitle='Page Not Found' />} />
          </Routes>
        </Layout>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
